import React, { useState, useMemo, memo } from "react";
import { View, Text, ActivityIndicator, TouchableOpacity } from "react-native";
import { Image } from "expo-image";
import { useFocusEffect } from "expo-router";
import { StyleSheet } from "@/utils/appStyleSheet";
import { Colors } from "@/constants/Colors";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { getCategoryConfig, formatCurrency } from "@/utils/myMoney";
import {
  formatDate,
  isToday,
  isYesterday,
  isThisMonth,
  isWithinMonths,
} from "@/utils/date";
import useFinanceDataStore from "@/store/financeData";
import { useBrandLogo } from "@/hooks/useBrandLogo";
import { Transaction } from "@/types/financeData";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import FBTrackingService from "@/services/fbTrackingService";
import Icons from "@/assets/icons";

const getInitialLetter = (name: string) =>
  (name || "?").charAt(0).toUpperCase();

// Group transactions by time period
interface GroupedTransactions {
  today: Transaction[];
  yesterday: Transaction[];
  thisMonth: Transaction[];
  threeMonths: Transaction[];
  sixMonths: Transaction[];
  oneYear: Transaction[];
}

const groupTransactions = (
  transactions: Transaction[],
): GroupedTransactions => {
  const groups: GroupedTransactions = {
    today: [],
    yesterday: [],
    thisMonth: [],
    threeMonths: [],
    sixMonths: [],
    oneYear: [],
  };

  for (const tx of transactions) {
    if (isToday(tx.date)) {
      groups.today.push(tx);
    } else if (isYesterday(tx.date)) {
      groups.yesterday.push(tx);
    } else if (isThisMonth(tx.date)) {
      groups.thisMonth.push(tx);
    } else if (isWithinMonths(tx.date, 3)) {
      groups.threeMonths.push(tx);
    } else if (isWithinMonths(tx.date, 6)) {
      groups.sixMonths.push(tx);
    } else if (isWithinMonths(tx.date, 12)) {
      groups.oneYear.push(tx);
    }
  }

  return groups;
};

// Chevron icon for expandable sections
const ChevronIcon = ({ isOpen }: { isOpen: boolean }) => (
  <View style={[styles.chevronContainer, isOpen && styles.chevronOpen]}>
    <Icons.ChevronRight width={16} height={16} color="#0F9129" />
  </View>
);

// Transaction item component
interface TransactionItemProps {
  transaction: Transaction;
  showDate?: boolean;
}

const TransactionItem = memo(function TransactionItem({
  transaction,
  showDate = false,
}: TransactionItemProps) {
  const merchantName = transaction.merchantName || transaction.name;
  const { icon } = useBrandLogo(merchantName);
  const categoryConfig = getCategoryConfig(transaction.category);

  return (
    <View style={styles.transactionRow}>
      <View style={styles.logoContainer}>
        {icon ? (
          <Image
            source={{ uri: icon }}
            style={styles.logo}
            contentFit="cover"
          />
        ) : (
          <View style={styles.logoFallback}>
            <Text style={styles.logoInitial}>
              {getInitialLetter(merchantName)}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.transactionInfo}>
        <View style={styles.transactionHeader}>
          <View style={styles.merchantRow}>
            <Text style={styles.merchantName} numberOfLines={1}>
              {merchantName}
            </Text>
            <View
              style={[
                styles.categoryBadge,
                { backgroundColor: categoryConfig.bgColor },
              ]}
            >
              <Text style={styles.categoryText}>
                {categoryConfig.emoji} {categoryConfig.name}
              </Text>
            </View>
          </View>
          <Text style={styles.amount}>
            {formatCurrency(transaction.amount)}
          </Text>
        </View>
        {showDate && (
          <Text style={styles.dateText}>{formatDate(transaction.date)}</Text>
        )}
      </View>
    </View>
  );
});

// Section component for grouped transactions
interface TransactionSectionProps {
  title: string;
  transactions: Transaction[];
  showDate?: boolean;
  defaultExpanded?: boolean;
  showViewAll?: boolean;
  maxItems?: number;
}

const TransactionSection = memo(function TransactionSection({
  title,
  transactions,
  showDate = false,
  defaultExpanded = true,
  showViewAll = false,
  maxItems,
}: TransactionSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showAll, setShowAll] = useState(false);

  if (transactions.length === 0) {
    return null;
  }

  // Filter out any null/undefined transactions to prevent crashes
  const validTransactions = transactions.filter((tx) => tx && tx.id);
  const displayedTransactions =
    showViewAll && !showAll && maxItems
      ? validTransactions.slice(0, maxItems)
      : validTransactions;

  return (
    <View style={styles.sectionCard}>
      <TouchableOpacity
        style={styles.sectionHeader}
        onPress={() => setIsExpanded(!isExpanded)}
      >
        <ChevronIcon isOpen={isExpanded} />
        <Text style={styles.sectionTitle}>{title}</Text>
      </TouchableOpacity>

      {isExpanded && (
        <View style={styles.transactionsList}>
          {displayedTransactions.map((tx, index) => (
            <View key={tx?.id || `tx-${index}`}>
              <TransactionItem transaction={tx} showDate={showDate} />
              {index < displayedTransactions.length - 1 && (
                <View style={styles.divider} />
              )}
            </View>
          ))}

          {showViewAll &&
            !showAll &&
            maxItems &&
            transactions.length > maxItems && (
              <TouchableOpacity
                style={styles.viewAllButton}
                onPress={() => setShowAll(true)}
              >
                <Text style={styles.viewAllText}>View all</Text>
              </TouchableOpacity>
            )}
        </View>
      )}
    </View>
  );
});

export default function TransactionsScreen() {
  const { financeData, isLoading } = useFinanceDataStore();

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("my-money-transactions");
    }, []),
  );

  const groupedTransactions = useMemo(() => {
    if (!financeData?.transactions) {
      return null;
    }
    return groupTransactions(financeData.transactions);
  }, [financeData?.transactions]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </View>
    );
  }

  if (!groupedTransactions) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyEmoji}>📭</Text>
        <Text style={styles.emptyTitle}>No transactions yet</Text>
        <Text style={styles.emptySubtitle}>
          Connect a bank account to see your transactions
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.content}>
      <TransactionSection
        title="Today"
        transactions={groupedTransactions.today}
        defaultExpanded={true}
      />

      <TransactionSection
        title="Yesterday"
        transactions={groupedTransactions.yesterday}
        defaultExpanded={true}
      />

      <TransactionSection
        title="This month"
        transactions={groupedTransactions.thisMonth}
        showDate={true}
        defaultExpanded={true}
        showViewAll={true}
        maxItems={4}
      />

      <TransactionSection
        title="3 months"
        transactions={groupedTransactions.threeMonths}
        showDate={true}
        defaultExpanded={false}
      />

      <TransactionSection
        title="6 months"
        transactions={groupedTransactions.sixMonths}
        showDate={true}
        defaultExpanded={false}
      />

      <TransactionSection
        title="1 year"
        transactions={groupedTransactions.oneYear}
        showDate={true}
        defaultExpanded={false}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: 16,
    gap: 12,
    paddingBottom: 100,
    width: SCREEN_WIDTH,
    alignSelf: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.background,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.background,
    padding: 32,
  },
  emptyEmoji: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#A3A3A3",
    textAlign: "center",
  },
  sectionCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    ...Shadows.card,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingBottom: 8,
    borderBottomColor: "#EBEBEB",
  },
  chevronContainer: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
    transform: [{ rotate: "90deg" }],
  },
  chevronOpen: {
    transform: [{ rotate: "90deg" }],
  },
  sectionTitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
  },
  transactionsList: {
    marginTop: 12,
    gap: 8,
  },
  transactionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    gap: 8,
  },
  logoContainer: {
    width: 40,
    height: 40,
  },
  logo: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  logoFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
  },
  logoInitial: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  transactionInfo: {
    flex: 1,
    gap: 2,
  },
  transactionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  merchantRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  merchantName: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    flexShrink: 1,
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 20,
  },
  categoryText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#000000",
  },
  amount: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
  },
  dateText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    opacity: 0.5,
  },
  divider: {
    height: 1,
    backgroundColor: "#F3F3F3",
  },
  viewAllButton: {
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#E1EFE4",
    alignSelf: "center",
  },
  viewAllText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#0F9129",
    textAlign: "center",
  },
});
